<template>
  <section class="repair-promo" aria-label="Consulta por reparación de celulares">
    <div class="container px-2 px-lg-4 py-0">
      <div
        class="repair-promo__hit"
        data-aos="fade"
        data-aos-duration="600"
        data-aos-easing="ease-out"
        data-aos-delay="60"
      >
        <div class="repair-promo__layout">
          <div
            class="repair-promo__media"
            data-aos="fade"
            data-aos-delay="120"
            data-aos-duration="600"
          >
            <div
              ref="sliderRef"
              class="repair-promo__slider"
              :style="{ '--slider-percent': `${sliderPercent}%` }"
              role="slider"
              aria-label="Comparar antes y después"
              aria-valuemin="0"
              aria-valuemax="100"
              :aria-valuenow="Math.round(sliderPercent)"
              tabindex="0"
              data-aos="fade"
              data-aos-delay="160"
              data-aos-duration="520"
              @pointerdown="onPointerDown"
              @pointermove="onPointerMove"
              @pointerup="onPointerUp"
              @pointerleave="onPointerUp"
              @pointercancel="onPointerUp"
              @keydown.left.prevent="nudge(-5)"
              @keydown.right.prevent="nudge(5)"
              @keydown.home.prevent="setSliderPercent(0)"
              @keydown.end.prevent="setSliderPercent(100)"
            >
              <!-- AFTER / imagen de fondo -->
              <img
                :src="afterImg"
                alt="Celular reparado"
                class="repair-promo__img"
                width="1200"
                height="420"
                loading="lazy"
                decoding="async"
                draggable="false"
              />

              <!-- BEFORE / imagen superior recortada -->
              <div class="repair-promo__before" aria-hidden="true">
                <img
                  :src="beforeImg"
                  alt=""
                  class="repair-promo__img"
                  width="1200"
                  height="420"
                  loading="lazy"
                  decoding="async"
                  draggable="false"
                />
              </div>

              <div class="repair-promo__divider" aria-hidden="true">
                <span class="repair-promo__knob">
                  <i class="fas fa-chevron-left"></i>
                  <i class="fas fa-chevron-right"></i>
                </span>
              </div>
            </div>
            <div
              class="repair-promo__hint"
              aria-hidden="true"
              data-aos="fade"
              data-aos-delay="220"
              data-aos-duration="520"
            >
              <i class="fas fa-arrows-alt-h"></i>
              Desliza para revelar el cambio
            </div>
          </div>

          <div
            class="repair-promo__content mb-lg-0 mb-4 text-center px-0"
            data-aos="fade"
            data-aos-delay="140"
            data-aos-duration="600"
          >
            <p class="repair-promo__eyebrow" data-aos="fade" data-aos-delay="180">
              Reparacion express
            </p>
            <h3 class="repair-promo__title" data-aos="fade" data-aos-delay="220">
              Tu equipo como nuevo
            </h3>
            <p class="repair-promo__text" data-aos="fade" data-aos-delay="260">
              Solucionamos los problemas mas comunes con repuestos de calidad y garantia. Servicio rapido, seguro y confiable.
            </p>
            <ul class="repair-promo__list">
              <li data-aos="fade" data-aos-delay="300">
                <i class="fas fa-mobile-alt"></i>
                Pantallas
              </li>
              <li data-aos="fade" data-aos-delay="340">
                <i class="fas fa-battery-three-quarters"></i>
                Baterias
              </li>
              <li data-aos="fade" data-aos-delay="380">
                <i class="fas fa-camera"></i>
                Camaras
              </li>
              <li data-aos="fade" data-aos-delay="420">
                <i class="fas fa-headphones-alt"></i>
                Mic. y Parlantes
              </li>
            </ul>
            <div class="w-100 d-flex justify-content-center mt-3" data-aos="fade" data-aos-delay="460">
              <button
                type="button"
                class="repair-promo__cta btn btn-primary"
                aria-haspopup="dialog"
                :aria-expanded="modalOpen"
                aria-controls="repair-contact-dialog"
                @click="modalOpen = true"
              >
                Consultar reparacion
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <ContactConsultModal
      v-model="modalOpen"
      dialog-id="repair-contact-dialog"
      :hero-image="repairImg"
      default-subject="Consulta por reparación de celular"
    />
  </section>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';

import beforeImg from '@/assets/images/repair-before.png';
import afterImg from '@/assets/images/repair-after.png';
import repairImg from '@/assets/images/repair-after.png';

import ContactConsultModal from '@/components/contact/ContactConsultModal.vue';

const modalOpen = ref(false);
const sliderPercent = ref(0);
const isDragging = ref(false);
const sliderRef = ref(null);
const autoAnimated = ref(false);
const userInteracted = ref(false);
let revealObserver = null;
let aosInHandler = null;
let rafId = null;

const clamp = (value, min = 0, max = 100) => {
  return Math.min(max, Math.max(min, value));
};

const updateSliderFromPointer = (event) => {
  const slider = sliderRef.value;
  if (!slider) return;

  const rect = slider.getBoundingClientRect();
  const x = event.clientX - rect.left;
  const percent = (x / rect.width) * 100;

  sliderPercent.value = clamp(percent);
};

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const animateSliderTo = (targetPercent, duration = 900) => {
  if (prefersReducedMotion()) {
    sliderPercent.value = clamp(targetPercent);
    autoAnimated.value = true;
    return;
  }

  if (rafId) {
    cancelAnimationFrame(rafId);
  }

  const startValue = sliderPercent.value;
  const endValue = clamp(targetPercent);
  const startTime = performance.now();

  const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

  const step = (now) => {
    const elapsed = now - startTime;
    const progress = Math.min(elapsed / duration, 1);
    sliderPercent.value = clamp(startValue + (endValue - startValue) * easeOutCubic(progress));

    if (progress < 1) {
      rafId = requestAnimationFrame(step);
    } else {
      autoAnimated.value = true;
    }
  };

  rafId = requestAnimationFrame(step);
};

const triggerAutoSlide = () => {
  if (autoAnimated.value || userInteracted.value) return;
  animateSliderTo(60);
};

const setSliderPercent = (value) => {
  userInteracted.value = true;
  if (rafId) {
    cancelAnimationFrame(rafId);
  }
  sliderPercent.value = clamp(value);
};

const onPointerDown = (event) => {
  isDragging.value = true;
  userInteracted.value = true;
  if (rafId) {
    cancelAnimationFrame(rafId);
  }

  event.currentTarget.setPointerCapture?.(event.pointerId);
  updateSliderFromPointer(event);
};

const onPointerMove = (event) => {
  if (!isDragging.value) return;

  updateSliderFromPointer(event);
};

const onPointerUp = (event) => {
  if (!isDragging.value) return;

  isDragging.value = false;
  event.currentTarget.releasePointerCapture?.(event.pointerId);
};

const nudge = (delta) => {
  setSliderPercent(sliderPercent.value + delta);
};

onMounted(() => {
  const slider = sliderRef.value;
  if (!slider) return;

  aosInHandler = () => triggerAutoSlide();
  slider.addEventListener('aos:in', aosInHandler);

  if ('IntersectionObserver' in window) {
    revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            triggerAutoSlide();
            revealObserver?.disconnect();
          }
        });
      },
      { threshold: 0.45 }
    );
    revealObserver.observe(slider);
  } else {
    setTimeout(() => triggerAutoSlide(), 400);
  }
});

onBeforeUnmount(() => {
  if (rafId) {
    cancelAnimationFrame(rafId);
  }

  if (revealObserver) {
    revealObserver.disconnect();
  }

  const slider = sliderRef.value;
  if (slider && aosInHandler) {
    slider.removeEventListener('aos:in', aosInHandler);
  }
});
</script>

<style scoped>
.repair-promo :deep(.container) {
  padding-left: max(1rem, env(safe-area-inset-left, 0px));
  padding-right: max(1rem, env(safe-area-inset-right, 0px));
}

.repair-promo__hit {
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: 18px;
  background: transparent;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.18);
}

.repair-promo__layout {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-areas:
    "content"
    "media";
  gap: clamp(1.2rem, 3vw, 2rem);
  padding: clamp(1rem, 2.5vw, 1.5rem);
}

.repair-promo__slider {
  position: relative;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  height: auto;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border-radius: inherit;
  cursor: grab;
  user-select: none;
  touch-action: none;
  isolation: isolate;
  background: transparent;
  border-radius: 8px;
}

.repair-promo__slider:active {
  cursor: grabbing;
}

.repair-promo__media {
  grid-area: media;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.repair-promo__slider:focus-visible {
  outline: 3px solid rgba(255, 255, 255, 0.7);
  outline-offset: 4px;
}

.repair-promo__img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  pointer-events: none;
  user-select: none;
  border-radius: inherit;
}

/* 
  CLAVE:
  La capa before NO cambia de tamaño.
  Siempre ocupa todo el slider y solo se recorta visualmente.
*/
.repair-promo__before {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  clip-path: inset(0 calc(100% - var(--slider-percent)) 0 0);
  z-index: 2;
}

.repair-promo__before .repair-promo__img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.repair-promo__divider {
  position: absolute;
  top: 0;
  bottom: 0;
  left: var(--slider-percent);
  z-index: 4;
  width: 2px;
  background: rgba(255, 255, 255, 0.9);
  transform: translateX(-50%);
  pointer-events: none;
}

.repair-promo__knob {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 46px;
  height: 46px;
  border-radius: 999px;
  transform: translate(-50%, -50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  color: #fff;
  background: rgba(0, 0, 0, 0.42);
  border: 1px solid rgba(255, 255, 255, 0.38);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.35);
  z-index: 5;
}

.repair-promo__knob i {
  font-size: 0.72rem;
  line-height: 1;
}

.repair-promo__content {
  grid-area: content;
  color: #fff;
  display: flex;
  flex-direction: column;
  justify-content: start;
  gap: 0.85rem;
  max-width: none;
}

.repair-promo__hint {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.65rem;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.55);
  letter-spacing: 0.02em;
  align-self: center;
  justify-content: center;
  width: 100%;
  text-align: center;
}

.repair-promo__hint i {
  color: var(--primary-color);
}

.repair-promo__eyebrow {
  margin: 0;
  font-size: 0.7rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 700;
}

.repair-promo__title {
  margin: 0;
  font-size: clamp(2.2rem, 4.2vw, 3rem);
  font-weight: 800;
  color: #fff;
}

.repair-promo__text {
  margin: 0;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
  font-size: 0.95rem;
}

.repair-promo__list {
  list-style: none;
  margin: 0 auto;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 0.85rem 1.2rem;
  max-width: 1000px;
}

.repair-promo__list li {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  font-size: 0.92rem;
  color: rgba(255, 255, 255, 0.9);
  flex-wrap: wrap;
}

.repair-promo__list i {
  width: 30px;
  height: 30px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.08);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-color);
}

.repair-promo__cta {
  align-self: flex-start;
  padding: 0.6rem 1.15rem;
  font-size: 0.95rem;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.repair-promo__cta:hover {
  transform: translateY(-1px);
}

.repair-promo__cta:focus-visible {
  outline: 3px solid rgba(255, 255, 255, 0.7);
  outline-offset: 3px;
}

@media (max-width: 767.98px) {
  .repair-promo__hit {
    border-radius: 16px;
  }

  .repair-promo__layout {
    padding-inline: 0;
  }

  .repair-promo__content {
    align-items: center;
    text-align: center;
    padding-inline: max(1rem, env(safe-area-inset-left, 0px));
  }

  .repair-promo__list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    justify-items: center;
  }

  .repair-promo__list li {
    justify-content: center;
  }

  .repair-promo__cta {
    align-self: center;
  }

  .repair-promo__slider {
    aspect-ratio: 4 / 3;
    border-radius: 0;
  }

  .repair-promo__img {
    object-position: center;
  }

  .repair-promo__knob {
    width: 40px;
    height: 40px;
  }

  .repair-promo__cta {
    padding: 0.52rem 0.95rem;
    font-size: 0.84rem;
  }
}
</style>